import React from 'react';

import { RefreshCcw, TriangleAlert } from 'lucide-react';

import { Button } from '@/new/components/shadcn/button';

interface ErrorPageProps {
  title: string | React.ReactNode;
  description: string | React.ReactNode;
}

const ErrorPage = ({ title, description }: ErrorPageProps) => {
  return (
    <div className="flex w-full justify-center px-4 pt-36">
      <div className="relative max-w-md rounded-lg border border-black/10 bg-gray-50/30 p-10 text-center">
        {/* Content */}
        <div className="relative flex flex-col items-center gap-7">
          {/* Icon with enhanced styling */}
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-teal-50/80">
            <TriangleAlert className="h-8 w-8 text-teal-600" />
          </div>

          {/* Text content */}
          <div className="flex flex-col items-center gap-2.5">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <div className="text-sm leading-relaxed text-gray-600">{description}</div>
          </div>
          {/* Refresh button */}
          <Button variant="primary" onClick={() => window.location.reload()}>
            Refresh <RefreshCcw />
          </Button>
        </div>
      </div>
    </div>
  );
};

type ErrorBoundaryProps = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (process.env.NODE_ENV !== 'production') {
      console.log(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <ErrorPage
            title="Something went wrong"
            description={
              <div>
                An unexpected error occurred. Please try refreshing the page. If this persists,{' '}
                <a className="text-blue-500 hover:underline" href="mailto:support@wexler.ai">
                  contact support
                </a>
                .
              </div>
            }
          />
        )
      );
    }
    return this.props.children;
  }
}
